import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import * as Colors from 'ui-library/Colors';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import APIUtil from 'util/APIUtil';
import {listToReadable, typeCountToReadable} from 'util/NameUtil';
import {SelectChangeEvent} from 'util/Types';
import fillFromWorklist from 'worklists/util/fillFromWorklist';
import {validateAllDNASequencesOrOligos} from 'worklists/validators/validateSequenceTypes';
import {WORKLIST_FILL_LOCATION, WorklistType} from 'worklists/WorklistConstants';

import * as BulkAssemblyActions from '../BulkAssemblyActions';
import {DataSourceType, LocalAssembly} from '../BulkAssemblyConstants';
import * as BulkAssemblyUtil from '../BulkAssemblyUtil';
import SearchableBySequenceFileTree from './SearchableBySequenceFileTree';

interface OtherSlotSourceProps {
  assembly: LocalAssembly;
  dataSourceId: string;
  handleDataSourceChange: (e: SelectChangeEvent) => void;
  otherSlotIndices: Array<number>;
  slotIdx: number;
}

function OtherSlotSource({
  assembly,
  dataSourceId,
  handleDataSourceChange,
  otherSlotIndices,
  slotIdx,
}: OtherSlotSourceProps): JSX.Element {
  return (
    <select value={dataSourceId} onChange={handleDataSourceChange}>
      {otherSlotIndices.length ? (
        <option value={UNLINK}>Unlink</option>
      ) : (
        <option value={dataSourceId} disabled={true}>
          Link To...
        </option>
      )}
      {assembly.dataSources.map((otherDataSource) => {
        const slotIndices = BulkAssemblyUtil.getSlotIndicesForDataSource(assembly, otherDataSource, slotIdx);
        if (slotIndices.length === 0) {
          return null;
        }
        const slotNames = listToReadable(
          slotIndices.map((idx) => BulkAssemblyUtil.getSlotName(assembly, idx))
        );
        return (
          <option key={otherDataSource.id} value={otherDataSource.id}>
            {slotNames}
          </option>
        );
      })}
    </select>
  );
}

function WorklistSource({
  fileIds,
  onUpdateFileIds,
}: {
  fileIds: Array<string>;
  onUpdateFileIds: (ids: Array<string>) => void;
}): JSX.Element {
  const handleFillFromWorklist = async (): Promise<void> => {
    const worklist = await fillFromWorklist(
      [WorklistType.BIOENTITY],
      WORKLIST_FILL_LOCATION.BULK_ASSEMBLY,
      validateAllDNASequencesOrOligos
    );
    if (worklist) {
      onUpdateFileIds(worklist.itemIds);
    }
  };

  return (
    <div className={css(styles.worklistSource)}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.SMALL}
        text={fileIds.length ? 'Select different worklist' : 'Select worklist'}
        onClick={handleFillFromWorklist}
        dataTestAttrs={{component: 'DataSourceSelector', element: 'selectWorklistButton'}}
      />
      <div className={css(styles.chipList)}>
        {fileIds.map((id) => (
          <BenchChip
            key={id}
            id={id}
            size={ChipSize.MINI}
            className={css(styles.chip)}
            onClick={() => {}}
            onRemove={() => {
              onUpdateFileIds(fileIds.filter((fileId) => fileId !== id));
            }}
          />
        ))}
      </div>
    </div>
  );
}

const UNLINK = 'UNLINK';

interface DataSourceSelectorProps {
  assembly: LocalAssembly;
  slotIdx: number;
}

function DataSourceSelector({assembly, slotIdx}: DataSourceSelectorProps): JSX.Element {
  const setTab = (newTab: DataSourceType): void => {
    BulkAssemblyActions.resetSlotDataSource(slotIdx, newTab);
  };

  const dataSource = BulkAssemblyUtil.getDataSourceForSlot(assembly, slotIdx);
  const otherSlotIndices = BulkAssemblyUtil.getSlotIndicesForDataSource(assembly, dataSource, slotIdx);

  const otherSlotNames = BulkAssemblyUtil.getSlotNames(assembly, otherSlotIndices);
  const counts = typeCountToReadable(
    _.countBy(dataSource.fileIds, (fileId) => (APIUtil.isFolder(fileId) ? 'folder' : 'file'))
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleDataSourceChange = (e: SelectChangeEvent) => {
    const dataSourceId = e.target.value;
    if (dataSourceId === UNLINK) {
      BulkAssemblyActions.resetSlotDataSource(slotIdx, DataSourceType.OTHER_SLOT);
    } else {
      BulkAssemblyActions.setSlotDataSource(slotIdx, dataSourceId);
    }
  };

  return (
    <div {...getTestProps({component: 'DataSourceSelector', element: 'root'})}>
      <div className={css(styles.selectedFilesSummary)}>
        <div className={css(styles.bold)}>Files for {BulkAssemblyUtil.getSlotName(assembly, slotIdx)}</div>
        <div className={css(styles.grey)}>
          <span>{counts || 'No files'}</span>
          {otherSlotIndices.length > 0 && <span> shared with {otherSlotNames}</span>}
        </div>
      </div>
      <TabContainer activeTab={assembly.slots[slotIdx].dataSourceType} onRequestTabChange={setTab}>
        <Tab tabKey={DataSourceType.FOLDERS} title="Folders">
          <SearchableBySequenceFileTree
            key={dataSource.id} // SearchableBySequenceFileTree is stateful so avoid re-use across data sources
            selectedIds={dataSource.fileIds}
            onChangeSelectedIds={(fileIds: Array<string>) => {
              BulkAssemblyActions.updateDataSource(dataSource.id, {fileIds});
            }}
            fetchLimit={20}
          />
        </Tab>
        {BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS && (
          <Tab tabKey={DataSourceType.WORKLIST} title="Worklist">
            <WorklistSource
              fileIds={dataSource.fileIds}
              onUpdateFileIds={(fileIds) => {
                BulkAssemblyActions.updateDataSource(dataSource.id, {fileIds});
              }}
            />
          </Tab>
        )}
        <Tab tabKey={DataSourceType.OTHER_SLOT} title="Another step">
          <OtherSlotSource
            assembly={assembly}
            dataSourceId={dataSource.id}
            handleDataSourceChange={handleDataSourceChange}
            otherSlotIndices={otherSlotIndices}
            slotIdx={slotIdx}
          />
        </Tab>
      </TabContainer>
    </div>
  );
}

export default DataSourceSelector;

const styles = StyleSheet.create({
  worklistSource: {
    display: 'flex',
    flexDirection: 'column',
  },
  chipList: {
    paddingTop: '5px',
  },
  chip: {
    margin: '3px',
  },
  selectedFilesSummary: {
    padding: '8px 0',
  },
  bold: {
    fontWeight: 600,
  },
  grey: {
    color: Colors.GRAY_DARKER,
  },
});
